import type { CSSProperties } from 'react';
import {
  formatTemperature,
  type TemperatureUnit,
} from '../../classroomPages/temperatureUnitStorage.js';
import styles from './Thermometer.module.css';

export interface ThermometerProps {
  value: number | null;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  /** Display unit for the readout + scale ticks. Defaults to 'F'. The
   *  slider itself always operates in °F (storage format); only the
   *  text rendering changes per unit. */
  unit?: TemperatureUnit;
}

/** Tick marks shown along the scale — in °F. 7 ticks spanning the
 *  full -10 → 110 range at 20° intervals. */
const TICKS_F = [-10, 10, 30, 50, 70, 90, 110];

/** Short value to show inside the slider bubble. Always returns a
 *  bare-degree string ("50°") with no F/C letter — keeps the bubble
 *  text compact. In Celsius mode the F-internal value is converted;
 *  in Words mode we fall back to °F so the kid sees a number alongside
 *  the descriptor word below. */
function bubbleLabel(fahrenheit: number, unit: TemperatureUnit): string {
  if (unit === 'C') {
    return `${Math.round(((fahrenheit - 32) * 5) / 9)}°`;
  }
  return `${Math.round(fahrenheit)}°`;
}

export function Thermometer({
  value,
  onChange,
  min = -10,
  max = 110,
  unit = 'F',
}: ThermometerProps) {
  // Default to the slider's minimum so a fresh classroom opens with
  // the bubble pinned at 0°F (or -10°F if min is lower). Sarah's rule:
  // "automatically start at 0".
  const v = value ?? 0;
  const isWords = unit === 'words';
  // Progress 0..100% across the slider range — drives the bubble
  // label's horizontal position so it tracks the thumb.
  const progressPct = ((v - min) / (max - min)) * 100;
  const tubeStyle: CSSProperties = { '--thumb-progress': `${progressPct}%` } as CSSProperties;
  return (
    <div className={styles.thermometer} data-testid="thermometer">
      <span className={styles.labelText}>Temperature</span>

      {/* In words mode we show °F ticks ABOVE the gauge so kids can
       *  see the temperature numbers AND the descriptor word at the
       *  same time — building the association between e.g. "55°F"
       *  and "Cool". F/C modes don't show this top scale (their
       *  numeric scale lives below the gauge already). */}
      {isWords ? (
        <div className={styles.scale} aria-hidden="true">
          {TICKS_F.map((tick) => (
            <span key={tick} className={styles.tick}>
              {formatTemperature(tick, 'F')}
            </span>
          ))}
        </div>
      ) : null}

      <label className={styles.label}>
        <div className={styles.tube} style={tubeStyle}>
          <input
            type="range"
            min={min}
            max={max}
            step={1}
            value={v}
            onChange={(e) => onChange(Number(e.target.value))}
            className={styles.slider}
            data-testid="thermometer-slider"
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={v}
          />
          {/* Glass highlight overlay — a soft top-edge gloss that
           *  doesn't intercept pointer events so the slider still
           *  works underneath. */}
          <span className={styles.gloss} aria-hidden="true" />
          {/* The temperature reading INSIDE the bubble. Positioned
           *  off the --thumb-progress CSS variable so it tracks the
           *  slider value. pointer-events: none so drag still works. */}
          <span className={styles.thumbLabel} aria-hidden="true">
            {bubbleLabel(v, unit)}
          </span>
        </div>
      </label>

      {/* Scale labels sit directly below the bar so kids can map the
       *  bubble position to a number/word. The user-set value sits
       *  below that, called out at heading size. */}
      {isWords ? (
        <div className={styles.wordScale} aria-hidden="true">
          <span>Cold</span>
          <span>Cool</span>
          <span>Warm</span>
          <span>Hot</span>
        </div>
      ) : (
        <div className={styles.scale} aria-hidden="true">
          {TICKS_F.map((tick) => (
            <span key={tick} className={styles.tick}>
              {formatTemperature(tick, unit)}
            </span>
          ))}
        </div>
      )}

      <div className={styles.readout} data-testid="thermometer-readout">
        {formatTemperature(value, unit)}
      </div>
    </div>
  );
}
